import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { projectAPI, waldflaechenAPI, constantsAPI } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  TreeDeciduous,
  MapPin,
  FileText,
  Calendar,
  CheckCircle2
} from 'lucide-react';
import { PriceEstimation } from '@/components/PriceEstimation';
import { GermanyMap } from '@/components/GermanyMap';
import { CO2Calculation } from '@/components/CO2Calculation';

const BUNDESLAENDER = [
  "Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen",
  "Hamburg", "Hessen", "Mecklenburg-Vorpommern", "Niedersachsen",
  "Nordrhein-Westfalen", "Rheinland-Pfalz", "Saarland", "Sachsen",
  "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen"
];

const BAUMARTEN = [
  "Fichte", "Kiefer", "Buche", "Eiche", "Tanne", "Lärche", "Douglasie",
  "Birke", "Ahorn", "Esche", "Erle", "Pappel", "Mischbestand"
];

const CreateProjectPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [kategorien, setKategorien] = useState([]);
  const [meinWald, setMeinWald] = useState([]);
  const [formData, setFormData] = useState({
    titel: '',
    beschreibung: '',
    kategorie: '',
    bundesland: '',
    ort: '',
    flaeche_ha: '',
    baumarten: [],
    zeitraum_start: '',
    zeitraum_ende: '',
    waldflaeche_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [katRes, waldRes] = await Promise.all([
        constantsAPI.getKategorien(),
        waldflaechenAPI.list()
      ]);
      setKategorien(katRes.data);
      setMeinWald(waldRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleBaumart = (baumart) => {
    setFormData(prev => ({
      ...prev,
      baumarten: prev.baumarten.includes(baumart)
        ? prev.baumarten.filter(b => b !== baumart)
        : [...prev.baumarten, baumart]
    }));
  };

  const handleWaldSelect = (waldId) => {
    if (waldId === 'none') {
      setFormData(prev => ({
        ...prev,
        waldflaeche_id: '',
        bundesland: prev.bundesland,
        ort: prev.ort,
        flaeche_ha: prev.flaeche_ha,
        baumarten: prev.baumarten
      }));
    } else {
      const wald = meinWald.find(w => w.id === waldId);
      if (wald) {
        setFormData(prev => ({
          ...prev,
          waldflaeche_id: waldId,
          bundesland: wald.bundesland,
          ort: wald.ort || '',
          flaeche_ha: wald.flaeche_ha.toString(),
          baumarten: wald.baumarten || []
        }));
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.titel || !formData.beschreibung || !formData.kategorie || 
        !formData.bundesland || !formData.flaeche_ha) {
      toast.error(t('toasts.pflichtfelderAusfuellen'));
      return;
    }

    setLoading(true);
    try {
      const response = await projectAPI.create({
        titel: formData.titel,
        beschreibung: formData.beschreibung,
        kategorie: formData.kategorie,
        bundesland: formData.bundesland,
        ort: formData.ort || null,
        flaeche_ha: parseFloat(formData.flaeche_ha),
        baumarten: formData.baumarten,
        zeitraum_start: formData.zeitraum_start || null,
        zeitraum_ende: formData.zeitraum_ende || null,
        waldflaeche_id: formData.waldflaeche_id || null
      });
      
      toast.success(t('toasts.projektErfolg'));
      navigate(`/projekte/${response.data.id}`);
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(error.response?.data?.detail || t('toasts.projektFehler'));
    } finally {
      setLoading(false);
    }
  };

  const totalSteps = 3;

  const canProceed = () => {
    if (step === 1) return formData.kategorie && formData.titel;
    if (step === 2) return formData.bundesland && formData.flaeche_ha;
    return true;
  };

  const renderStep = () => {
    if (step === 1) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="inline-flex p-4 rounded-2xl bg-primary/10 mb-4">
              <FileText className="h-10 w-10 text-primary" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">
              Was möchten Sie tun?
            </h2>
            <p className="text-gray-500">Beschreiben Sie Ihr Waldprojekt</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Projektart *</Label>
              <div className="grid grid-cols-2 gap-3">
                {kategorien.map((kat) => (
                  <div
                    key={kat.id}
                    onClick={() => handleChange('kategorie', kat.id)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.kategorie === kat.id
                        ? 'border-primary bg-primary/5'
                        : 'border-warm-200 hover:border-primary/30'
                    }`}
                  >
                    <p className="font-medium text-gray-900">{kat.name}</p>
                    <p className="text-xs text-gray-500 mt-1">{kat.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Projekttitel *</Label>
              <Input
                value={formData.titel}
                onChange={(e) => handleChange('titel', e.target.value)}
                placeholder="z.B. Durchforstung 5 ha Fichtenbestand"
                data-testid="project-title-input"
              />
              <p className="text-xs text-gray-500">
                Ein klarer Titel hilft Anbietern, Ihr Projekt zu verstehen
              </p>
            </div>

            <div className="space-y-2">
              <Label>Beschreibung *</Label>
              <Textarea
                value={formData.beschreibung}
                onChange={(e) => handleChange('beschreibung', e.target.value)}
                placeholder={t('placeholders.projektBeschreiben')}
                rows={5}
                data-testid="project-description-input"
              />
            </div>
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="inline-flex p-4 rounded-2xl bg-secondary/10 mb-4">
              <MapPin className="h-10 w-10 text-secondary" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">
              Wo befindet sich Ihr Wald?
            </h2>
            <p className="text-gray-500">Standort und Walddaten</p>
          </div>

          {meinWald.length > 0 && (
            <div className="space-y-2 p-4 bg-primary/5 rounded-xl">
              <Label>{t('placeholders.ausMeinemWald')}</Label>
              <Select
                value={formData.waldflaeche_id || 'none'}
                onValueChange={handleWaldSelect}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('placeholders.waldflaecheAuswaehlen')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Neue Fläche eingeben</SelectItem>
                  {meinWald.map((wald) => (
                    <SelectItem key={wald.id} value={wald.id}>
                      {wald.name} ({wald.flaeche_ha} ha, {wald.bundesland})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bundesland *</Label>
                <Select
                  value={formData.bundesland}
                  onValueChange={(v) => handleChange('bundesland', v)}
                >
                  <SelectTrigger data-testid="project-bundesland">
                    <SelectValue placeholder={t('placeholders.auswaehlen')} />
                  </SelectTrigger>
                  <SelectContent>
                    {BUNDESLAENDER.map((land) => (
                      <SelectItem key={land} value={land}>{land}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Ort</Label>
                <Input
                  value={formData.ort}
                  onChange={(e) => handleChange('ort', e.target.value)}
                  placeholder="z.B. Garmisch"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Fläche in Hektar *</Label>
              <Input
                type="number"
                step="0.1"
                value={formData.flaeche_ha}
                onChange={(e) => handleChange('flaeche_ha', e.target.value)}
                placeholder="z.B. 5.5"
                data-testid="project-flaeche"
              />
            </div>

            <div className="space-y-2">
              <Label>Baumarten</Label>
              <div className="grid grid-cols-3 gap-2">
                {BAUMARTEN.map((baumart) => (
                  <div
                    key={baumart}
                    onClick={() => toggleBaumart(baumart)}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                      formData.baumarten.includes(baumart)
                        ? 'bg-secondary/10 border-secondary'
                        : 'border-warm-200 hover:border-secondary/50'
                    }`}
                  >
                    <Checkbox checked={formData.baumarten.includes(baumart)} />
                    {baumart}
                  </div>
                ))}
              </div>
            </div>

            {/* Map visualization */}
            {formData.bundesland && (
              <div className="mt-6">
                <Label className="mb-3 block">Standort auf der Karte</Label>
                <GermanyMap 
                  selectedBundesland={formData.bundesland} 
                  height={200}
                  className="bg-warm-100 rounded-xl p-4"
                />
              </div>
            )}

            {/* Price estimation preview */}
            {formData.kategorie && formData.bundesland && formData.flaeche_ha && (
              <div className="mt-6">
                <PriceEstimation
                  flaeche_ha={formData.flaeche_ha}
                  kategorie={formData.kategorie}
                  bundesland={formData.bundesland}
                  baumart={formData.baumarten[0] || null}
                  compact={true}
                />
              </div>
            )}

            {/* CO2 Calculation for CO2 or Aufforstung projects */}
            {(formData.kategorie === 'co2_projekt' || formData.kategorie === 'aufforstung') && formData.flaeche_ha && (
              <div className="mt-4">
                <CO2Calculation
                  flaeche_ha={formData.flaeche_ha}
                  baumart={formData.baumarten[0] || null}
                  years={10}
                  compact={true}
                />
              </div>
            )}
          </div>
        </div>
      );
    }

    if (step === 3) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="inline-flex p-4 rounded-2xl bg-accent/10 mb-4">
              <Calendar className="h-10 w-10 text-accent" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">
              Wann soll es losgehen?
            </h2>
            <p className="text-gray-500">Zeitrahmen für Ihr Projekt</p>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Gewünschter Start</Label>
                <Input
                  type="date"
                  value={formData.zeitraum_start}
                  onChange={(e) => handleChange('zeitraum_start', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Spätester Abschluss</Label>
                <Input
                  type="date"
                  value={formData.zeitraum_ende}
                  onChange={(e) => handleChange('zeitraum_ende', e.target.value)}
                />
              </div>
            </div>

            <div className="p-4 bg-warm-100 rounded-xl">
              <p className="text-sm text-gray-600">
                <strong>Tipp:</strong> Lassen Sie den Zeitraum flexibel, wenn Sie keine 
                festen Termine haben. Das erhöht Ihre Chancen auf gute Angebote.
              </p>
            </div>
          </div>

          {/* Summary */}
          <div className="mt-8 p-6 bg-primary/5 rounded-xl border border-primary/20">
            <h3 className="font-semibold text-gray-900 mb-4">Zusammenfassung</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Projektart:</dt>
                <dd className="font-medium">{kategorien.find(k => k.id === formData.kategorie)?.name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Titel:</dt>
                <dd className="font-medium">{formData.titel}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Standort:</dt>
                <dd className="font-medium">{formData.ort ? `${formData.ort}, ` : ''}{formData.bundesland}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Fläche:</dt>
                <dd className="font-medium">{formData.flaeche_ha} ha</dd>
              </div>
              {formData.baumarten.length > 0 && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Baumarten:</dt>
                  <dd className="font-medium">{formData.baumarten.join(', ')}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Price Estimation in Summary */}
          {formData.kategorie && formData.bundesland && formData.flaeche_ha && (
            <div className="mt-4">
              <PriceEstimation
                flaeche_ha={formData.flaeche_ha}
                kategorie={formData.kategorie}
                bundesland={formData.bundesland}
                baumart={formData.baumarten[0] || null}
                compact={false}
              />
            </div>
          )}

          {/* CO2 Calculation in Summary for CO2 or Aufforstung */}
          {(formData.kategorie === 'co2_projekt' || formData.kategorie === 'aufforstung') && formData.flaeche_ha && (
            <div className="mt-4">
              <CO2Calculation
                flaeche_ha={formData.flaeche_ha}
                baumart={formData.baumarten[0] || null}
                years={10}
                compact={false}
              />
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto" data-testid="create-project-page">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-2 w-16 rounded-full transition-colors ${
                  i + 1 <= step ? 'bg-primary' : 'bg-warm-300'
                }`}
              />
            ))}
          </div>
          <p className="text-sm text-center text-gray-500">
            Schritt {step} von {totalSteps}
          </p>
        </div>

        <Card>
          <CardContent className="p-6 md:p-8">
            {renderStep()}

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-warm-200">
              {step > 1 ? (
                <Button
                  variant="ghost"
                  onClick={() => setStep(step - 1)}
                  disabled={loading}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Zurück
                </Button>
              ) : (
                <Button variant="ghost" onClick={() => navigate('/projekte')}>
                  Abbrechen
                </Button>
              )}

              {step < totalSteps ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="bg-primary hover:bg-primary-600"
                >
                  Weiter
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !canProceed()}
                  className="bg-primary hover:bg-primary-600"
                  data-testid="create-project-submit"
                >
                  {loading ? (
                    t('toasts.wirdErstellt')
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Projekt erstellen
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
};

export default CreateProjectPage;
